import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, CircularProgress } from '@mui/material'
import { supabase } from '../utils/supabase'

// Square redirects here after OAuth authorization:
//   https://nami.pet/oauth/square/callback?code=...&state=...
//
// The `state` param carries the merchant's Supabase ID so we know
// which merchant to link the Square account to.

export default function SquareOAuthCallback() {
	const navigate = useNavigate()
	const [status, setStatus] = useState('loading') // 'loading' | 'success' | 'error'
	const [errorMessage, setErrorMessage] = useState('')
	const hasRun = useRef(false)

	useEffect(() => {
		// Guard against React StrictMode double-invocation — Square codes are single-use
		if (hasRun.current) return
		hasRun.current = true

		const run = async () => {
			const params = new URLSearchParams(window.location.search)
			const code = params.get('code')
			const state = params.get('state') // merchantId stored here

			if (!code || !state) {
				setErrorMessage('Missing authorization code or state parameter.')
				setStatus('error')
				return
			}

			try {
				const { data, error } = await supabase.functions.invoke('square-oauth-callback', {
					body: {
						code,
						merchantId: state,
						redirectUri: `${window.location.origin}/oauth/square/callback`,
					},
				})

				if (error || data?.error) {
					console.error('Square OAuth error:', error || data?.error)
					setErrorMessage(data?.error || error?.message || 'Failed to connect Square account.')
					setStatus('error')
					return
				}

				setStatus('success')
				// Redirect back to settings after a short delay
				setTimeout(() => navigate('/dashboard'), 2000)
			} catch (err) {
				console.error('Square OAuth callback error:', err)
				setErrorMessage(err.message || 'Unexpected error during Square connection.')
				setStatus('error')
			}
		}

		run()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<Box
			sx={{
				minHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '16px',
				bgcolor: '#F7F6F3',
				p: 4,
			}}
		>
			{status === 'loading' && (
				<>
					<CircularProgress sx={{ color: '#9F7AEA' }} />
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '16px', color: '#4A5568' }}>
						Connecting your Square account…
					</Typography>
				</>
			)}

			{status === 'success' && (
				<>
					<Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#C6F6D5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none">
							<path d="M5 13l4 4L19 7" stroke="#22543D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</Box>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '18px', color: '#2D3748' }}>
						Square connected!
					</Typography>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#718096' }}>
						Redirecting you back to settings…
					</Typography>
				</>
			)}

			{status === 'error' && (
				<>
					<Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#FED7D7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none">
							<path d="M18 6L6 18M6 6l12 12" stroke="#9B2335" strokeWidth="2.5" strokeLinecap="round" />
						</svg>
					</Box>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '18px', color: '#2D3748' }}>
						Connection failed
					</Typography>
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', color: '#718096', textAlign: 'center', maxWidth: 400 }}>
						{errorMessage}
					</Typography>
					<Typography
						onClick={() => navigate('/dashboard')}
						sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#9F7AEA', cursor: 'pointer', textDecoration: 'underline' }}
					>
						Return to dashboard
					</Typography>
				</>
			)}
		</Box>
	)
}
